import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { ArrowLeft, Loader2, Users, RefreshCw, CheckCircle2, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { apiCall } from '@/lib/api';

interface DupeSide {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  status: string;
  joined_at: string;
  notes: string | null;
  counts: {
    payments: number;
    check_ins: number;
    class_registrations: number;
    waiver_signatures: number;
    notifications: number;
    subscriptions: number;
    class_passes: number;
    event_registrations: number;
    store_sales: number;
  };
}
interface DupePair { tier: 1 | 2; a: DupeSide; b: DupeSide }

export default function MemberDupes() {
  const { getToken } = useAuth();
  const [items, setItems] = useState<DupePair[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [merging, setMerging] = useState<string | null>(null);
  const [merged, setMerged] = useState<string[]>([]);
  const [filter, setFilter] = useState<'all' | 'tier1' | 'tier2'>('all');

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const token = await getToken();
      const data = await apiCall<{ items: DupePair[] }>('/v1/members/duplicates', { bearerToken: token });
      setItems(data.items);
      setMerged([]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { void load(); }, [load]);

  async function merge(keep: string, loser: string) {
    const pair = items.find((p) => (p.a.id === keep && p.b.id === loser) || (p.b.id === keep && p.a.id === loser));
    if (!pair) return;
    if (!confirm(`Merge "${pair.b.id === loser ? pair.b.name : pair.a.name}" into the kept record? All payments, check-ins, subscriptions, and passes from the loser get reassigned. The loser record is deleted permanently.`)) return;
    setMerging(`${keep}:${loser}`);
    try {
      const token = await getToken();
      await apiCall(`/v1/members/${keep}/merge`, {
        method: 'POST', bearerToken: token, body: { loser_id: loser },
      });
      setMerged((m) => [...m, loser]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Merge failed');
    } finally {
      setMerging(null);
    }
  }

  const visible = items.filter((p) => {
    if (merged.includes(p.a.id) || merged.includes(p.b.id)) return false;
    if (filter === 'tier1') return p.tier === 1;
    if (filter === 'tier2') return p.tier === 2;
    return true;
  });
  const tier1Count = items.filter((p) => p.tier === 1).length;
  const tier2Count = items.filter((p) => p.tier === 2).length;

  return (
    <div className="space-y-4 max-w-5xl">
      <Button variant="ghost" size="sm" asChild><Link to="/dashboard/members"><ArrowLeft className="mr-1 h-4 w-4" /> Members</Link></Button>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><Users className="h-6 w-6" /> Possible duplicates</h1>
          <p className="text-sm text-muted-foreground">
            Review side-by-side. Click <strong>Keep this</strong> on the record you want to keep — the other side's check-ins, payments, subscriptions, etc. will be reassigned and the loser deleted.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
          Refresh
        </Button>
      </div>

      <div className="border-b flex items-center gap-1">
        {([
          ['all', `All (${items.length})`],
          ['tier1', `Tier 1 — same email (${tier1Count})`],
          ['tier2', `Tier 2 — name match only (${tier2Count})`],
        ] as Array<[typeof filter, string]>).map(([key, label]) => (
          <button
            key={key} onClick={() => setFilter(key)}
            className={
              'px-3 py-2 text-sm border-b-2 -mb-px ' +
              (filter === key ? 'border-foreground font-medium' : 'border-transparent text-muted-foreground hover:text-foreground')
            }>{label}</button>
        ))}
      </div>

      {error && <Card className="border-destructive/40 bg-destructive/5"><CardContent className="py-3 text-sm text-destructive flex items-center gap-2"><AlertCircle className="h-4 w-4" /> {error}</CardContent></Card>}

      {visible.length === 0 && !loading && (
        <Card><CardContent className="py-12 text-center text-sm text-muted-foreground">
          <CheckCircle2 className="mx-auto mb-3 h-8 w-8 text-emerald-600" />
          {items.length === 0 ? 'No duplicates found.' : 'No duplicates left in this filter.'}
        </CardContent></Card>
      )}

      {visible.map((p, i) => (
        <PairCard
          key={`${p.a.id}:${p.b.id}`}
          pair={p}
          merging={merging === `${p.a.id}:${p.b.id}` || merging === `${p.b.id}:${p.a.id}`}
          onMerge={merge}
          index={i + 1}
        />
      ))}
    </div>
  );
}

function PairCard({ pair, merging, onMerge, index }: {
  pair: DupePair; merging: boolean; onMerge: (keep: string, loser: string) => void; index: number;
}) {
  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-sm font-medium">
            #{index}. <span className="font-semibold">{pair.a.name}</span>
            <Badge variant={pair.tier === 1 ? 'default' : 'outline'} className="ml-2">
              {pair.tier === 1 ? 'Same email' : 'Name only'}
            </Badge>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <SidePanel side={pair.a} onKeep={() => onMerge(pair.a.id, pair.b.id)} merging={merging} />
          <SidePanel side={pair.b} onKeep={() => onMerge(pair.b.id, pair.a.id)} merging={merging} />
        </div>
      </CardContent>
    </Card>
  );
}

function SidePanel({ side, onKeep, merging }: { side: DupeSide; onKeep: () => void; merging: boolean }) {
  const c = side.counts;
  const totalActivity = c.payments + c.check_ins + c.subscriptions + c.class_passes + c.event_registrations + c.store_sales + c.class_registrations;
  return (
    <Card className="border-muted">
      <CardContent className="p-3 space-y-2 text-sm">
        <div className="flex items-center justify-between">
          <Link to={`/dashboard/members/${side.id}`} className="font-medium hover:underline truncate">{side.name}</Link>
          <Badge variant={side.status === 'active' ? 'default' : 'outline'} className="capitalize text-xs">{side.status}</Badge>
        </div>
        <div className="text-xs text-muted-foreground space-y-0.5">
          <div>{side.email || <span className="italic">no email</span>}</div>
          <div>{side.phone || <span className="italic">no phone</span>}</div>
          <div>joined {new Date(side.joined_at.replace(' ', 'T')).toLocaleDateString()}</div>
        </div>
        <div className="text-xs flex flex-wrap gap-x-3 gap-y-0.5 pt-2 border-t">
          <Count label="checkins" n={c.check_ins} />
          <Count label="payments" n={c.payments} />
          <Count label="subs" n={c.subscriptions} />
          <Count label="passes" n={c.class_passes} />
          <Count label="events" n={c.event_registrations} />
          <Count label="classes" n={c.class_registrations} />
          <Count label="waivers" n={c.waiver_signatures} />
          <Count label="sales" n={c.store_sales} />
          <Count label="notifs" n={c.notifications} />
        </div>
        <div className="pt-1 flex items-center justify-between">
          <span className="text-xs text-muted-foreground">total {totalActivity} rows</span>
          <Button size="sm" onClick={onKeep} disabled={merging} className="h-7">
            {merging ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
            Keep this
          </Button>
        </div>
        {side.notes && (
          <details className="text-xs text-muted-foreground"><summary className="cursor-pointer">notes</summary>
            <pre className="whitespace-pre-wrap mt-1 text-[10px]">{side.notes}</pre>
          </details>
        )}
      </CardContent>
    </Card>
  );
}

function Count({ label, n }: { label: string; n: number }) {
  return <span className={n > 0 ? 'tabular-nums' : 'text-muted-foreground tabular-nums'}>{n} {label}</span>;
}
